import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'react-final-form';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import AdminFields from 'components/admin/AdminFields';
import FormSurface from 'components/shared/surfaces/FormSurface';
import UserEditHeader from 'components/shared/UserEditHeader';
import PrimaryLoadingButton from 'components/shared/buttons/PrimaryLoadingButton';
import SecondaryButtonLoading from 'components/shared/buttons/SecondaryLoadingButton';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import validateEditAdminForm from 'common/validations/form/editAdmin';
import { updateAdminInitialValues } from 'common/form/initial-values/update';
import getDataToSave from 'common/form/dataToSave';
import { generatePassword, getFieldsToUpdate } from 'common/form/utils';
import { adminFindFields } from 'common/constants/query-fields/admin';
import requestStatus from 'common/constants/status';
import routes from 'common/routes';
import AppBarContext from 'context/AppBarContext';
import adminSlice from 'store/modules/admin';

const requiredFields = ['first_name', 'last_name', 'email', 'birthdate', 'ui_language'];

function AdminEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['buttons', 'common', 'routes']);
  const { id } = useParams();
  const pageTitle = t('administrator.edit', { ns: 'routes' });
  const [initialValues, setInitialValues] = useState({});
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);

  const admin = useSelector((state) => state.admin.currentAdmin);
  const status = useSelector((state) => state.admin.status.general);
  const previousStatus = React.useRef(status);
  // El `find` inicial y el `update` comparten `status.general`; sin este guard el
  // loading→success del find dispararía la redirección y rebotaría a la lista.
  const hasSubmitted = React.useRef(false);

  useEffect(() => {
    dispatch(adminSlice.actions.find({ fields: adminFindFields, id }));
  }, []);

  useEffect(() => {
    if (admin && Object.keys(admin).length > 0 && admin.id === id) {
      const userData = updateAdminInitialValues(admin);
      setInitialValues(userData);
    }
  }, [admin]);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(pageTitle);
  }, [pageTitle, setAppBarButton, setAppBarTitle]);

  // Navegar a la lista SOLO después de una edición (submit) exitosa.
  useEffect(() => {
    if (
      hasSubmitted.current &&
      previousStatus.current === requestStatus.loading &&
      status === requestStatus.success
    ) {
      navigate(routes.listAdmin.path);
    }
    previousStatus.current = status;
  }, [status, navigate]);

  const onEdit = useCallback(
    (values) => {
      const fieldsToUpdate = getFieldsToUpdate(values, initialValues);
      const dataToSave = getDataToSave(fieldsToUpdate, 'user');
      hasSubmitted.current = true;
      dispatch(adminSlice.actions.update({ id, input: dataToSave }));
    },
    [dispatch, id, initialValues]
  );

  return (
    <>
      <SEOPageTitle title={pageTitle} />
      <Form
        initialValues={initialValues}
        mutators={{
          generatePassword
        }}
        onSubmit={onEdit}
        validate={validateEditAdminForm}
        render={({ form, handleSubmit, invalid, pristine, values }) => (
          <form onSubmit={handleSubmit}>
            <FormSurface>
              {/* Cabecera premium unificada: avatar EDITABLE (crop) + nombre + rol. */}
              <UserEditHeader
                editable
                firstName={values.first_name || ''}
                lastName={values.last_name || ''}
                email={values.email || ''}
                roleLabel={t('roles.administrator', { ns: 'common', defaultValue: 'Administrador' })}
              />
              <AdminFields form={form} requiredFields={requiredFields} />
            </FormSurface>
            <Box sx={{ marginTop: '40px', textAlign: 'right' }}>
              <SecondaryButtonLoading
                component={RouterLink}
                disabled={status === requestStatus.loading}
                style={{ marginRight: '32px' }}
                label={t('cancel', { ns: 'buttons' })}
                to={routes.listAdmin.path}
                type="button"
              />
              <PrimaryLoadingButton
                disabled={invalid || pristine || status === requestStatus.loading}
                loading={status === requestStatus.loading}
                label={t('administrator.edit', { ns: 'buttons' })}
                type="submit"
              />
            </Box>
          </form>
        )}
      />
    </>
  );
}

export default AdminEdit;
